/**
 * The standalone session: for people who signed up at this app's own address
 * rather than through a Rumio tenant site. It is a Bearer token kept in
 * localStorage, so it survives closing the tab.
 *
 * It is read ONLY when the app is not inside an iframe. Embedded in the
 * tenant site the app is same-site with its own address, so storage is
 * shared, and a leftover standalone session must never take over from the
 * Rumio session the host provides.
 */

import { inIframe } from "./iframe-bridge";

const STORAGE_KEY = "lettrova.standalone_session";
export const STANDALONE_ISSUER = "lettrova-standalone";

/** Routes a signed-out visitor may see; everything else sends them to /login. */
export const PUBLIC_PATHS = ["/signup", "/login", "/auth/verify", "/welcome"];

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function payloadOf(token: string): Record<string, unknown> | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    return JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

export function isStandaloneToken(token: string | null): boolean {
  return Boolean(token && payloadOf(token)?.iss === STANDALONE_ISSUER);
}

export function readStandaloneToken(): string | null {
  if (inIframe()) return null;
  try {
    const token = localStorage.getItem(STORAGE_KEY);
    if (!token) return null;
    const exp = payloadOf(token)?.exp;
    return typeof exp === "number" && exp * 1000 > Date.now() ? token : null;
  } catch {
    return null;
  }
}

export function writeStandaloneToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(STORAGE_KEY, token);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage blocked — the session lasts until the tab closes */
  }
}

/** Trade a still-valid session for a fresh one. Null when it can't be refreshed. */
export async function refreshStandaloneToken(current: string): Promise<string | null> {
  try {
    const resp = await fetch("/api/standalone/auth/refresh", {
      method: "POST",
      headers: { Authorization: `Bearer ${current}` },
    });
    if (!resp.ok) return null;
    const body = (await resp.json()) as { data?: { token?: string } };
    return body.data?.token ?? null;
  } catch {
    return null;
  }
}

/** Where to send someone after signing in: only ever a path inside this app. */
export function safeNext(next: string | null): string {
  return next && next.startsWith("/") && !next.startsWith("//") && !isPublicPath(next.split("?")[0]) ? next : "/home";
}
